import type { Dispatch } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@/components/atoms/Card';
import { Radio, RadioGroup } from '@/components/atoms/RadioGroup';
import type { OverviewCage } from '@/domain/fishGroups';

import type {
    BioscopeMovementDecision,
    OperateWizardAction,
    WizardDestination,
    WizardSource,
} from '../../operateWizardReducer';

import styles from '../../operateModals.module.scss';

/** The value of the "stay in this pen" option; the others are cage ids. */
const STAY = 'stay';

interface Props {
    sources: WizardSource[];
    destinations: WizardDestination[];
    /** Every cage of the source FG's home location. Source for bioscope ids. */
    locationCages: OverviewCage[];
    /** Decisions made so far. Keyed by bioscopeId for fast lookup. */
    bioscopeMovements: BioscopeMovementDecision[];
    dispatch: Dispatch<OperateWizardAction>;
}

interface SourceCageEntry {
    cageId: number;
    cageName: string;
    bioscopeIds: number[];
}

interface DestinationOption {
    cageId: number;
    cageName: string;
    /** Bioscope ids currently present at this cage — informational. */
    currentBioscopeIds: number[];
}

/**
 * Shared bioscope-movement step for the composition operations (split,
 * merge, split-and-merge). Operator decides per bioscope at each source
 * cage whether it stays put or moves to one of the operation's destination
 * cages. Defaults to "Stay" — moves are an explicit opt-in.
 *
 * The component is purely UX glue: it lists known bioscopes (read from
 * locationCages.activeBioscopes for each source cage) and forwards radio
 * picks to the reducer via setBioscopeMovementTarget / clearBioscopeMovement.
 * Visibility of the step itself is decided one level up — by the time this
 * component renders, the modal has already established that at least one
 * source cage carries a bioscope and the operator has permission.
 */
export default function BioscopeMovementsStep(props: Props) {
    const { sources, destinations, locationCages, bioscopeMovements, dispatch } = props;
    const { t } = useTranslation('fishGroups');

    // Map each source cage → its bioscope ids. Skip sources whose cage is
    // either unknown or doesn't carry a bioscope: those entries would render
    // an empty card and clutter the view.
    const sourceEntries: SourceCageEntry[] = [];
    for (const source of sources) {
        if (source.currentCageId == null) continue;
        const cage = locationCages.find((c) => c.cageId === source.currentCageId);
        if (!cage || cage.activeBioscopes.length === 0) continue;
        sourceEntries.push({
            cageId: cage.cageId,
            cageName: cage.cageName,
            bioscopeIds: cage.activeBioscopes,
        });
    }

    // Destination options are derived from the wizard state — they are the
    // cages the operator already chose for this operation. Filter out
    // destinations that aren't fully defined yet (no cageId picked) and the
    // ones that point at a source cage (which is the bioscope's own cage —
    // a no-op for the move).
    const destinationOptions: DestinationOption[] = [];
    const seen = new Set<number>();
    for (const dest of destinations) {
        if (dest.cageId == null || seen.has(dest.cageId)) continue;
        seen.add(dest.cageId);
        const cage = locationCages.find((c) => c.cageId === dest.cageId);
        if (!cage) continue;
        destinationOptions.push({
            cageId: cage.cageId,
            cageName: cage.cageName,
            currentBioscopeIds: cage.activeBioscopes,
        });
    }

    const targetByBioscope = new Map(bioscopeMovements.map((m) => [m.bioscopeId, m.targetCageId]));

    return (
        <section className={styles.body}>
            <div>
                <h4 className={styles.heading}>{t('actions.operate.bioscopes.heading')}</h4>
                <p className={styles.description}>{t('actions.operate.bioscopes.description')}</p>
            </div>

            {sourceEntries.map((entry) => {
                const otherDestinations = destinationOptions.filter(
                    (d) => d.cageId !== entry.cageId
                );
                return (
                    <Card
                        as="article"
                        key={entry.cageId}
                        radius="sm"
                        padding="xs"
                        className={styles.destinationCard}>
                        <header className={styles.destinationHeader}>
                            <h5 className={styles.destinationTitle}>
                                {t('actions.operate.bioscopes.sourceCardTitle', {
                                    cageName: entry.cageName,
                                })}
                            </h5>
                        </header>
                        {entry.bioscopeIds.map((bioscopeId) => {
                            const groupName = `bioscope-${bioscopeId}`;
                            const selectedTarget = targetByBioscope.get(bioscopeId) ?? null;
                            return (
                                <div key={bioscopeId} className={styles.bioscopeRow}>
                                    <span
                                        id={`${groupName}-label`}
                                        className={styles.bioscopeRowLabel}>
                                        {t('actions.operate.bioscopes.bioscopeLabel', {
                                            id: bioscopeId,
                                        })}
                                    </span>
                                    {/* Each option keeps its own handler on click, which the
                                        group also fires when the arrows move the choice. */}
                                    <RadioGroup
                                        direction="horizontal"
                                        name={groupName}
                                        aria-labelledby={`${groupName}-label`}
                                        value={
                                            selectedTarget === null ? STAY : String(selectedTarget)
                                        }>
                                        <Radio
                                            value={STAY}
                                            labelClassName={styles.bioscopeOption}
                                            onClick={() =>
                                                dispatch({
                                                    type: 'clearBioscopeMovement',
                                                    bioscopeId,
                                                })
                                            }
                                            label={t('actions.operate.bioscopes.stayOption', {
                                                cageName: entry.cageName,
                                            })}
                                        />
                                        {otherDestinations.map((dest) => (
                                            <Radio
                                                key={dest.cageId}
                                                value={String(dest.cageId)}
                                                labelClassName={styles.bioscopeOption}
                                                onClick={() =>
                                                    dispatch({
                                                        type: 'setBioscopeMovementTarget',
                                                        bioscopeId,
                                                        targetCageId: dest.cageId,
                                                    })
                                                }
                                                label={
                                                    dest.currentBioscopeIds.length > 0
                                                        ? t(
                                                              'actions.operate.bioscopes.moveOptionWithCurrent',
                                                              {
                                                                  cageName: dest.cageName,
                                                                  currentIds:
                                                                      dest.currentBioscopeIds.join(
                                                                          ', '
                                                                      ),
                                                              }
                                                          )
                                                        : t(
                                                              'actions.operate.bioscopes.moveOption',
                                                              {
                                                                  cageName: dest.cageName,
                                                              }
                                                          )
                                                }
                                            />
                                        ))}
                                    </RadioGroup>
                                </div>
                            );
                        })}
                    </Card>
                );
            })}
        </section>
    );
}
